import { useEffect, useCallback } from "react";

/**
 * Hook to manage local storage debounced autosave and session draft recovery.
 */
export const useLocalDraftAutoSave = ({
  selectedChild,
  activeWorkspaceEnv,
  activeWorkspaceCode,
  activeWorkspaceFiles,
  projectName,
  projectId,
  lastSavedCloudCode,
  baselineCode,
  setProjectName,
  setProjectId,
  setLoadedProjectData,
  setActiveWorkspaceCode,
  setActiveWorkspaceFiles,
  setLastSavedCloudCode,
  setBaselineCode,
  openDialog,
}) => {
  const getDraftKey = useCallback(() => {
    if (!selectedChild) return null;
    const cid = selectedChild.id || selectedChild.uid;
    return `practido_draft_${cid}_${activeWorkspaceEnv}`;
  }, [selectedChild, activeWorkspaceEnv]);

  const restoreDraftData = useCallback(
    (parsed) => {
      if (!parsed) return;
      setProjectName(parsed.projectName || "Unnamed Project");
      setProjectId(parsed.projectId || null);

      if (Array.isArray(parsed.files) && parsed.files.length > 0) {
        if (setActiveWorkspaceFiles) setActiveWorkspaceFiles(parsed.files);
        const entryFile = parsed.files.find((f) => f.isEntry) || parsed.files[0];
        const content = parsed.code || entryFile?.content || null;
        setLoadedProjectData({ code: content, files: parsed.files });
        setActiveWorkspaceCode(content);
      } else {
        if (setActiveWorkspaceFiles) setActiveWorkspaceFiles(null);
        setLoadedProjectData(parsed.code || null);
        setActiveWorkspaceCode(parsed.code || null);
      }

      setLastSavedCloudCode(parsed.lastSavedCloudCode || null);
      setBaselineCode(parsed.baselineCode || null);
    },
    [
      setProjectName,
      setProjectId,
      setLoadedProjectData,
      setActiveWorkspaceCode,
      setActiveWorkspaceFiles,
      setLastSavedCloudCode,
      setBaselineCode,
    ]
  );

  const clearDraft = useCallback(() => {
    const draftKey = getDraftKey();
    if (draftKey) {
      localStorage.removeItem(draftKey);
    }
  }, [getDraftKey]);

  // 1. Check for unsaved draft in localStorage when child or environment changes
  useEffect(() => {
    const draftKey = getDraftKey();
    if (!draftKey) return;

    try {
      const savedDraft = localStorage.getItem(draftKey);
      if (savedDraft) {
        const parsed = JSON.parse(savedDraft);

        const normalize = (c) =>
          typeof c === "object" ? JSON.stringify(c) : String(c || "");
        const isDraftDirty =
          normalize(parsed.code) !== normalize(parsed.baselineCode);

        if (isDraftDirty && parsed.code) {
          openDialog({
            type: "RESTORE_DRAFT",
            title: "Unsaved Work Found",
            message:
              "We found an unsaved draft from your last session. Would you like to restore it, or start a completely new project?",
            confirmText: "Restore",
            confirmClass: "bg-purple-600 hover:bg-purple-500 text-white",
            cancelText: "Start new",
            payload: parsed,
            showInput: false,
            inputValue: "",
          });
        } else {
          restoreDraftData(parsed);
        }
      }
    } catch (e) {
      console.warn("Failed to load local draft from localStorage:", e);
    }
  }, [selectedChild, activeWorkspaceEnv]); // eslint-disable-line

  // 2. Debounced save to localStorage (1.5 seconds)
  useEffect(() => {
    if (!activeWorkspaceCode) return;

    const draftKey = getDraftKey();
    if (!draftKey) return;

    const timer = setTimeout(() => {
      const draft = {
        projectName,
        projectId,
        code: activeWorkspaceCode,
        files: activeWorkspaceFiles || undefined,
        lastSavedCloudCode,
        baselineCode,
        timestamp: Date.now(),
      };
      localStorage.setItem(draftKey, JSON.stringify(draft));
    }, 1500);

    return () => clearTimeout(timer);
  }, [
    activeWorkspaceCode,
    activeWorkspaceFiles,
    projectName,
    projectId,
    lastSavedCloudCode,
    baselineCode,
    getDraftKey,
  ]);

  return {
    getDraftKey,
    restoreDraftData,
    clearDraft,
  };
};

export default useLocalDraftAutoSave;
